import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
} from '@angular/core';
import { cloneDeep as _cloneDeep } from 'lodash';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxCheckBoxComponent } from 'devextreme-angular/ui/check-box';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { DxScrollViewComponent } from 'devextreme-angular/ui/scroll-view';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import {
    ColumnView,
    ColumnViewsDoc,
    ORDERING_FORCED_COLUMNS,
    newColumnViewId,
} from '../column-views';

export interface ColumnChoice {
    field: string;
    caption: string;
}

// Which column shows in which view, as one table of check boxes: columns down
// the side, views across the top. Edits a copy, so closing it changes nothing.
@Component({
    selector: 'app-column-views-popup',
    templateUrl: './column-views-popup.component.html',
    styleUrls: ['./column-views-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxButtonComponent,
        DxCheckBoxComponent,
        DxPopupComponent,
        DxScrollViewComponent,
        DxTextBoxComponent,
    ],
})
export class ColumnViewsPopupComponent {
    @Input() saving = false;
    @Output() save = new EventEmitter<ColumnViewsDoc>();

    visible = false;
    columns: ColumnChoice[] = [];
    draft: ColumnViewsDoc = { views: [], orderingViewId: null };

    open(doc: ColumnViewsDoc, columns: ColumnChoice[]): void {
        this.columns = columns;
        this.draft = _cloneDeep(doc);
        this.visible = true;
    }

    close(): void {
        this.visible = false;
    }

    isForced(field: string): boolean {
        return ORDERING_FORCED_COLUMNS.includes(field);
    }

    isChecked(view: ColumnView, field: string): boolean {
        return view.columns.includes(field);
    }

    onColumnToggled(view: ColumnView, field: string, checked: boolean): void {
        view.columns = checked
            ? [...view.columns.filter((name) => name !== field), field]
            : view.columns.filter((name) => name !== field);
    }

    addView(): void {
        const view: ColumnView = {
            id: newColumnViewId(),
            name: `View ${this.draft.views.length + 1}`,
            columns: [...(this.draft.views[0]?.columns ?? [])],
        };
        this.draft.views = [...this.draft.views, view];
    }

    removeView(view: ColumnView): void {
        this.draft.views = this.draft.views.filter((v) => v.id !== view.id);
        if (this.draft.orderingViewId === view.id) {
            this.draft.orderingViewId = null;
        }
    }

    // One view at most opens with ordering mode; none leaves the view alone.
    onOrderingViewToggled(view: ColumnView, checked: boolean): void {
        if (checked) {
            this.draft.orderingViewId = view.id;
        } else if (this.draft.orderingViewId === view.id) {
            this.draft.orderingViewId = null;
        }
    }

    get canSave(): boolean {
        return (
            !this.saving &&
            this.draft.views.length > 0 &&
            this.draft.views.every((view) => view.name.trim() !== '')
        );
    }

    confirm(): void {
        if (!this.canSave) {
            return;
        }
        this.save.emit({
            ...this.draft,
            views: this.draft.views.map((view) => ({
                ...view,
                name: view.name.trim(),
            })),
        });
    }
}
